import { makeAutoObservable } from 'mobx';
import { Api } from '../api';
import { PortfolioStore, PortfolioStoreHydration } from './portfolio-store';
import { TradeStore, TradeStoreHydration } from './trade-store';
import { CurrencyStore } from './currency-store';
import { AuthStore, AuthStoreHydration } from './auth-store';
import { ThemeStore, ThemeStoreHydration } from './theme-store';
import { StatisticStore, StatisticStoreHydration } from './statistic/statistic-store';
import { AlertStore } from './alert-store';
import { DividendsStore, DividendsStoreHydration } from './dividends-store';
import { CookieService } from '../services/cookie-service';
import { StripeStore } from './stripe-store';
import { InvestmentsStore, InvestmentsStoreHydration } from './statistic/investments-store';
import { UserStore, UserStoreHydration } from './user-store';
import { ProfitsStore, ProfitsStoreHydration } from './profits-store';
import { TaxReportStore, TaxReportStoreHydration } from './statistic/tax-report-store';
import { PremiumPlusDialogStore } from './premium-plus-dialog-store';
import { PartnerDialogStore } from './partner-dialog-store';
import { ActiveUsersStore, ActiveUsersStoreHydration } from './admin/user/active-users-store';
import { WaitingUsersStore, WaitingUsersStoreHydration } from './admin/user/waiting-users-store';
import { SymbolsStore, SymbolsStoreHydration } from './symbols-store';
import { PortfolioListStore } from './admin/portfolio/portfolio-list-store';
import { WatchlistStore, WatchlistStoreHydration } from './watchlist-store';
import { AdminStatisticsStore } from './admin/statistic/admin-statistics-store';
import { AdminAnalyticsStore } from './admin/analytics/admin-analytics-store';
import { PrivateStore } from './private-store';
import { OffboardingStore } from './offboarding-store';
import { BannerYearlyPremiumStore } from './banner-yearly-premium-store';
import { LeaderboardStore } from './leaderboard-store';
import { HomepageStore, HomepageStoreHydration } from './homepage-store';
import { NotConfirmedUsersStore, NotConfirmedUsersStoreHydration } from './admin/user/not-confirmed-users-store';
import { SymbolAlternativesStore, SymbolAlternativesStoreHydration } from './symbol-alternatives-store';
import { PortfolioIntegrationStore } from './portfolio-integration-store';
import ApiClient from '../api-client';
import { PortfolioValueCalculationInfoDialogStore } from './portfolio-value-calculation-info-dialog-store';
import { MailStore } from './mail-store';
import { TermsAgreementDialogStore } from './terms-agreement-dialog-store';
import { AffiliateStore } from './affiliate-store';
import { SymbolPriceUpdateStore } from './symbol-price-update-store';
import { OnboardingStore } from './onboarding-store';
import { PersonalAssetStore } from './personal-asset-store';
import { PersonalAssetHistoryStore } from './personal-asset-history-store';
import { SymbolDetailFairPriceDialogStore } from './symbol-detail-fair-price-dialog-store';
import { ConfirmStore } from './confirm-store';
import { InvestownAssetStore } from './investown-asset-store';
import { InvestownAssetHistoryStore } from './investown-asset-history-store';
import { CompetitionDialogStore, CompetitionDialogStoreHydration } from './competition-dialog-store';
import { BannerInvestownStore } from './banner-investown-store';
import { SourceSurveyStore } from './source-survey-store';
import { CriticalNotificationStore } from './critical-notification-store';
import { ImporterStore } from './importer-store';
import { AdminCriticalNotificationStore } from './admin/critical-notification-store';
import { AdminImporterStore } from './admin/importer-store';
import { AdminCampaignLinkStore } from './admin/campaign-link-store';
import { ReferralStore } from './referral-store';
import { NotificationStore } from './notification-store';
import { PostCheckoutFlowStore } from './post-checkout-flow-store';
import { NotificationHistoryStore } from './notification-history-store';

const SKELETON_LOAD_DELAY_MS = 250;

export type RootStoreHydration = {
    availablePremiumLookupKeys?: string[];
    portfolioStore?: PortfolioStoreHydration;
    tradeStore?: TradeStoreHydration;
    authStore?: AuthStoreHydration;
    statisticStore?: StatisticStoreHydration;
    themeStore?: ThemeStoreHydration;
    dividendsStore?: DividendsStoreHydration;
    investmentsStore?: InvestmentsStoreHydration;
    userStore?: UserStoreHydration;
    activeUsersStore?: ActiveUsersStoreHydration;
    waitingUsersStore?: WaitingUsersStoreHydration;
    notConfirmedUsersStore?: NotConfirmedUsersStoreHydration;
    profitsStore?: ProfitsStoreHydration;
    symbolsStore?: SymbolsStoreHydration;
    taxReportStore?: TaxReportStoreHydration;
    watchlistStore?: WatchlistStoreHydration;
    premiumPlusDialogStore?: PremiumPlusDialogStore;
    partnerDialogStore?: PartnerDialogStore;
    portfolioValueCalculationInfoDialogStore?: PortfolioValueCalculationInfoDialogStore;
    offboardingStore?: OffboardingStore;
    sourceSurveyStore?: SourceSurveyStore;
    bannerYearlyPremiumStore?: BannerYearlyPremiumStore;
    bannerInvestownStore?: BannerInvestownStore;
    leaderboardStore?: LeaderboardStore;
    homepageStore?: HomepageStoreHydration;
    alternativeSymbols?: SymbolAlternativesStoreHydration;
    mailStore?: MailStore;
    termsAgreementDialogStore?: TermsAgreementDialogStore;
    onboardingStore?: OnboardingStore;
    criticalNotificationStore?: CriticalNotificationStore;
    symbolDetailFairPriceDialogStore?: SymbolDetailFairPriceDialogStore;
    confirmStore?: ConfirmStore;
    competitionDialogStore?: CompetitionDialogStoreHydration;
};

export class RootStore {
    authStore: AuthStore;

    portfolioStore: PortfolioStore;

    portfolioIntegrationStore: PortfolioIntegrationStore;

    portfolioListStore: PortfolioListStore;

    tradeStore: TradeStore;

    currencyStore: CurrencyStore;

    themeStore: ThemeStore;

    statisticStore: StatisticStore;

    alertStore: AlertStore;

    dividendsStore: DividendsStore;

    stripeStore: StripeStore;

    investmentsStore: InvestmentsStore;

    taxReportStore: TaxReportStore;

    activeUsersStore: ActiveUsersStore;

    waitingUsersStore: WaitingUsersStore;

    notConfirmedUsersStore: NotConfirmedUsersStore;

    symbolsStore: SymbolsStore;

    userStore: UserStore;

    watchlistStore: WatchlistStore;

    profitsStore: ProfitsStore;

    premiumPlusDialogStore: PremiumPlusDialogStore;

    partnerDialogStore: PartnerDialogStore;

    portfolioValueCalculationInfoDialogStore: PortfolioValueCalculationInfoDialogStore;

    adminStatisticsStore: AdminStatisticsStore;

    adminAnalyticsStore: AdminAnalyticsStore;

    offboardingStore: OffboardingStore;

    sourceSurveyStore: SourceSurveyStore;

    privateStore: PrivateStore;

    bannerYearlyPremiumStore: BannerYearlyPremiumStore;

    bannerInvestownStore: BannerInvestownStore;

    leaderboardStore: LeaderboardStore;

    homepageStore: HomepageStore;

    symbolAlternativesStore: SymbolAlternativesStore;

    termsAgreementDialogStore: TermsAgreementDialogStore;

    mailStore: MailStore;

    affiliateStore: AffiliateStore;

    referralStore: ReferralStore;

    onboardingStore: OnboardingStore;

    criticalNotificationStore: CriticalNotificationStore;

    adminCriticalNotificationStore: AdminCriticalNotificationStore;

    adminImporterStore: AdminImporterStore;

    adminCampaignLinkStore: AdminCampaignLinkStore;

    notificationStore: NotificationStore;

    postCheckoutFlowStore: PostCheckoutFlowStore;

    notificationHistoryStore: NotificationHistoryStore;

    importerStore: ImporterStore;

    host: string;

    api: Api;

    apiClient: ApiClient;

    cookieService: CookieService;

    confirmStore: ConfirmStore;

    symbolPriceUpdateStore: SymbolPriceUpdateStore;

    personalAssetStore: PersonalAssetStore;

    personalAssetHistoryStore: PersonalAssetHistoryStore;

    symbolDetailFairPriceDialogStore: SymbolDetailFairPriceDialogStore;

    investownAssetStore: InvestownAssetStore;

    investownAssetHistoryStore: InvestownAssetHistoryStore;

    competitionDialogStore: CompetitionDialogStore;

    constructor(host: string, api: Api, apiClient: ApiClient) {
        this.authStore = new AuthStore(this);
        this.portfolioStore = new PortfolioStore(this);
        this.affiliateStore = new AffiliateStore(this);
        this.referralStore = new ReferralStore(this);
        this.portfolioIntegrationStore = new PortfolioIntegrationStore(this);
        this.tradeStore = new TradeStore(this);
        this.themeStore = new ThemeStore(this);
        this.currencyStore = new CurrencyStore(this);
        this.statisticStore = new StatisticStore(this);
        this.alertStore = new AlertStore(this);
        this.dividendsStore = new DividendsStore(this);
        this.stripeStore = new StripeStore(this);
        this.investmentsStore = new InvestmentsStore(this);
        this.taxReportStore = new TaxReportStore(this);
        this.waitingUsersStore = new WaitingUsersStore(this);
        this.activeUsersStore = new ActiveUsersStore(this);
        this.notConfirmedUsersStore = new NotConfirmedUsersStore(this);
        this.userStore = new UserStore(this);
        this.symbolsStore = new SymbolsStore(this);
        this.profitsStore = new ProfitsStore(this);
        this.watchlistStore = new WatchlistStore(this);
        this.premiumPlusDialogStore = new PremiumPlusDialogStore(this);
        this.partnerDialogStore = new PartnerDialogStore(this);
        this.portfolioValueCalculationInfoDialogStore = new PortfolioValueCalculationInfoDialogStore(this);
        this.portfolioListStore = new PortfolioListStore(this);
        this.adminStatisticsStore = new AdminStatisticsStore(this);
        this.adminAnalyticsStore = new AdminAnalyticsStore(this);
        this.offboardingStore = new OffboardingStore(this);
        this.sourceSurveyStore = new SourceSurveyStore(this);
        this.bannerYearlyPremiumStore = new BannerYearlyPremiumStore(this);
        this.bannerInvestownStore = new BannerInvestownStore(this);
        this.leaderboardStore = new LeaderboardStore(this);
        this.homepageStore = new HomepageStore(this);
        this.symbolAlternativesStore = new SymbolAlternativesStore(this);
        this.mailStore = new MailStore(this);
        this.personalAssetStore = new PersonalAssetStore(this);
        this.personalAssetHistoryStore = new PersonalAssetHistoryStore(this);
        this.termsAgreementDialogStore = new TermsAgreementDialogStore(this);
        this.onboardingStore = new OnboardingStore(this);
        this.criticalNotificationStore = new CriticalNotificationStore(this);
        this.adminCriticalNotificationStore = new AdminCriticalNotificationStore(this);
        this.adminImporterStore = new AdminImporterStore(this);
        this.adminCampaignLinkStore = new AdminCampaignLinkStore(this);
        this.privateStore = new PrivateStore(this, api.cookieService);
        this.host = host;
        this.api = api;
        this.apiClient = apiClient;
        this.cookieService = api.cookieService;
        this.symbolPriceUpdateStore = new SymbolPriceUpdateStore(this);
        this.symbolDetailFairPriceDialogStore = new SymbolDetailFairPriceDialogStore(this);
        this.confirmStore = new ConfirmStore(this);
        this.investownAssetStore = new InvestownAssetStore(this);
        this.investownAssetHistoryStore = new InvestownAssetHistoryStore(this);
        this.competitionDialogStore = new CompetitionDialogStore(this);
        this.importerStore = new ImporterStore(this);
        this.notificationStore = new NotificationStore(this);
        this.postCheckoutFlowStore = new PostCheckoutFlowStore(this);
        this.notificationHistoryStore = new NotificationHistoryStore(this);

        makeAutoObservable(this, {
            host: false,
            api: false,
            cookieService: false,
        });
    }

    rehydrate(data: RootStoreHydration): void {
        if (data.portfolioStore) {
            this.portfolioStore.rehydrate(data.portfolioStore);
        }

        if (data.authStore) {
            this.authStore.rehydrate(data.authStore);
        }

        if (data.themeStore) {
            this.themeStore.rehydrate(data.themeStore);
        }

        if (data.userStore) {
            this.userStore.rehydrate(data.userStore);
        }

        if (data.homepageStore) {
            this.homepageStore.rehydrate(data.homepageStore);
        }

        if (data.competitionDialogStore) {
            this.competitionDialogStore.rehydrate(data.competitionDialogStore);
        }
    }

    loadWithDelay<T>(operation: () => Promise<T>): Promise<T> {
        try {
            const start = performance.now();
            const result = operation();
            const end = performance.now();

            const duration = end - start;
            const waitTime = SKELETON_LOAD_DELAY_MS - duration;

            // eslint-disable-next-line no-promise-executor-return
            const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

            return wait(waitTime >= 0 ? waitTime : 0).then(() => {
                return result;
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            throw e;
        }
    }
}
